
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PremiumBot, RiskLevel } from "@/types/premium-bots";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ColorScheme } from "@/types/trader";
import { useCreatePremiumBot, useUpdatePremiumBot, useAdminPremiumBotDetail } from "@/hooks/use-admin-premium-bots";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

// Schema for form validation
const formSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  status: z.enum(["active", "inactive"]),
  color_scheme: z.enum([
    "royal_purple",
    "ocean_blue",
    "emerald_green",
    "ruby_red",
    "amber_gold",
    "midnight_black",
    "coral_orange",
  ] as const),
  trading_style: z.enum([
    "Momentum",
    "Scalping",
    "Swing",
    "Grid",
    "Trend",
  ] as const),
  risk: z.enum(["Low", "Medium", "High"] as const),
  profit_share: z.coerce.number().min(0).max(100),
  min_capital: z.coerce.number().min(0),
  potential_profit: z.string().optional(),
  max_drawdown: z.string().optional(),
  winrate: z.string().optional(),
  profit_factor: z.string().optional(),
  timeframe: z.string().optional(),
  pairs: z.string().optional(),
  markets: z.string().optional(),
  features: z.string().optional(),
  is_featured: z.boolean().default(false),
  is_new: z.boolean().default(true),
  is_best_seller: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

interface PremiumBotFormProps {
  botId?: string;
  isEdit?: boolean;
}

export const PremiumBotForm = ({ botId, isEdit = false }: PremiumBotFormProps) => {
  const navigate = useNavigate();
  const { data: botData, isLoading: isLoadingBot } = useAdminPremiumBotDetail(
    isEdit ? botId : undefined
  );
  
  const createBotMutation = useCreatePremiumBot();
  const updateBotMutation = useUpdatePremiumBot();

  // Initialize form with default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      status: "active",
      color_scheme: "royal_purple",
      trading_style: "Momentum",
      risk: "Medium",
      profit_share: 0,
      min_capital: 100,
      potential_profit: "",
      max_drawdown: "",
      winrate: "",
      profit_factor: "",
      timeframe: "",
      pairs: "",
      markets: "",
      features: "",
      is_featured: false,
      is_new: true,
      is_best_seller: false,
    },
  });

  // Format array data from comma-separated string
  const formatArrayData = (data: string | undefined): string[] => {
    if (!data) return [];
    return data
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== "");
  };

  // Update form with bot data when editing
  useEffect(() => {
    if (isEdit && botData) {
      // Ensure premiumDetails is an object, and cast it with the correct type to resolve TypeScript errors
      const premiumDetails = (botData.premiumDetails || {}) as {
        trading_style?: string;
        risk?: string;
        profit_share?: number;
        min_capital?: number;
        potential_profit?: string;
        max_drawdown?: string;
        winrate?: string;
        profit_factor?: string;
        timeframe?: string;
        pairs?: string[];
        markets?: string[];
        features?: string[];
      };
      
      // Format array data for display in form - safely handle undefined arrays
      const pairsString = premiumDetails.pairs && Array.isArray(premiumDetails.pairs) 
        ? premiumDetails.pairs.join(", ")
        : "";
        
      const marketsString = premiumDetails.markets && Array.isArray(premiumDetails.markets)
        ? premiumDetails.markets.join(", ")
        : "";
        
      const featuresString = premiumDetails.features && Array.isArray(premiumDetails.features)
        ? premiumDetails.features.join(", ")
        : "";

      form.reset({
        name: botData.name || "",
        description: botData.description || "",
        status: botData.status || "active",
        color_scheme: botData.color_scheme || "royal_purple",
        trading_style: (premiumDetails.trading_style as any) || "Momentum",
        risk: (premiumDetails.risk as any) || "Medium",
        profit_share: premiumDetails.profit_share || 0,
        min_capital: premiumDetails.min_capital || 100,
        potential_profit: premiumDetails.potential_profit || "",
        max_drawdown: premiumDetails.max_drawdown || "",
        winrate: premiumDetails.winrate || "",
        profit_factor: premiumDetails.profit_factor || "",
        timeframe: premiumDetails.timeframe || "",
        pairs: pairsString,
        markets: marketsString,
        features: featuresString,
        is_featured: botData.is_featured || false,
        is_new: botData.is_new || false,
        is_best_seller: botData.is_best_seller || false,
      });
    }
  }, [botData, isEdit, form]);

  // Handle form submission
  const onSubmit = async (values: FormValues) => {
    try {
      const botData: Partial<PremiumBot> = {
        name: values.name,
        description: values.description,
        status: values.status,
        color_scheme: values.color_scheme as ColorScheme,
        is_featured: values.is_featured,
        is_new: values.is_new,
        is_best_seller: values.is_best_seller,
        premiumDetails: {
          bot_id: botId || "", // Will be set by backend for new bots
          trading_style: values.trading_style,
          risk: values.risk,
          profit_share: values.profit_share,
          min_capital: values.min_capital,
          // Exclude generated columns - they will be calculated automatically
          // subscribers_total and connected_accounts_total are generated columns
          potential_profit: values.potential_profit,
          max_drawdown: values.max_drawdown,
          winrate: values.winrate,
          profit_factor: values.profit_factor,
          timeframe: values.timeframe,
          pairs: formatArrayData(values.pairs),
          markets: formatArrayData(values.markets),
          features: formatArrayData(values.features),
        },
      };

      console.log('Submitting bot data:', botData);

      if (isEdit && botId) {
        await updateBotMutation.mutateAsync({ botId, botData });
        toast.success("Premium bot updated successfully");
        navigate("/admin/premium-bots");
      } else {
        await createBotMutation.mutateAsync(botData);
        toast.success("Premium bot created successfully");
        navigate("/admin/premium-bots");
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // Show loading state while fetching bot data
  if (isEdit && isLoadingBot) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bot Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter bot name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter bot description"
                      className="resize-none min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select bot status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="color_scheme"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Color Scheme</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select color scheme" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="royal_purple">Royal Purple</SelectItem>
                        <SelectItem value="ocean_blue">Ocean Blue</SelectItem>
                        <SelectItem value="emerald_green">Emerald Green</SelectItem>
                        <SelectItem value="ruby_red">Ruby Red</SelectItem>
                        <SelectItem value="amber_gold">Amber Gold</SelectItem>
                        <SelectItem value="midnight_black">Midnight Black</SelectItem>
                        <SelectItem value="coral_orange">Coral Orange</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Trading Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="trading_style"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trading Style</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select trading style" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Momentum">Momentum</SelectItem>
                        <SelectItem value="Scalping">Scalping</SelectItem>
                        <SelectItem value="Swing">Swing</SelectItem>
                        <SelectItem value="Grid">Grid</SelectItem>
                        <SelectItem value="Trend">Trend</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="risk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Risk Level</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select risk level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="profit_share"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Profit Share (%)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number"
                        placeholder="Enter profit share percentage"
                        min="0"
                        max="100"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Percentage of profits shared with subscribers
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="min_capital"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Minimum Capital</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter minimum required capital"
                        min="0"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Minimum capital required to use this bot
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="potential_profit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Potential Profit</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., 15-20% monthly" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="max_drawdown"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Drawdown</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., 5-10%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="winrate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Win Rate</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., 65%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="profit_factor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Profit Factor</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., 1.75" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="timeframe"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Timeframe</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., H1, D1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pairs"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trading Pairs</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter trading pairs separated by commas (e.g., BTC/USD, ETH/USD)"
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter pairs separated by commas
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="markets"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Markets</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter markets separated by commas (e.g., Crypto, Forex, Stocks)"
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter markets separated by commas
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="features"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Features</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter features separated by commas (e.g., Stop-loss, Take-profit, Trailing stop)"
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter features separated by commas
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Display Options</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="is_featured"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Featured Bot</FormLabel>
                    <FormDescription>
                      Display this bot as featured in the marketplace
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_new"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">New Bot</FormLabel>
                    <FormDescription>
                      Display this bot as newly added in the marketplace
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_best_seller"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Best Seller</FormLabel>
                    <FormDescription>
                      Display this bot as a best seller in the marketplace
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/premium-bots")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createBotMutation.isPending || updateBotMutation.isPending}
          >
            {(createBotMutation.isPending || updateBotMutation.isPending) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {isEdit ? "Update Bot" : "Create Bot"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
